import { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router'

import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '@/components/ui/input-otp'
import { Label } from '@/components/ui/label'
import { captureException } from '@/domains/observability'
import { PasswordInput } from '@/domains/signup'

import { authClient } from './auth-client'
import {
  CAPTCHA_FAILED,
  captchaHeaders,
  TurnstileWidget
} from './turnstile-widget'

const RESEND_COOLDOWN_SEC = 30
const OTP_LENGTH = 6

interface ResetState {
  email?: string
}

export function ResetPasswordForm() {
  const navigate = useNavigate()
  const location = useLocation()
  const email = (location.state as ResetState | null)?.email ?? null

  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [resending, setResending] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const [error, setError] = useState('')

  useEffect(() => {
    if (cooldown <= 0) return
    const id = setTimeout(() => setCooldown(seconds => seconds - 1), 1000)
    return () => clearTimeout(id)
  }, [cooldown])

  /** the code + new password are entered together here. without an email in
   * history state there's nothing to reset against, so send them back to
   * request a fresh code (a hard refresh drops the state, same as /verify). */
  if (!email) {
    return <Navigate to='/forgot-password' replace />
  }

  async function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    if (!email) return
    setError('')

    if (code.length !== OTP_LENGTH) {
      setError('Enter the 6-digit code from your email.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }

    setSubmitting(true)
    try {
      const { error: authError } = await authClient.emailOtp.resetPassword({
        email,
        otp: code,
        password
      })
      if (authError) {
        setSubmitting(false)
        setError(
          authError.message ??
            'That code is invalid or expired. Request a new one.'
        )
        return
      }
      void navigate('/login?reset=ok', { replace: true })
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)))
      setSubmitting(false)
      setError('Something went wrong. Please try again.')
    }
  }

  async function handleResend() {
    if (!email) return
    setResending(true)
    setError('')
    try {
      const captcha = await captchaHeaders()
      if (!captcha) {
        setError(CAPTCHA_FAILED)
        return
      }
      const { error: authError } =
        await authClient.emailOtp.requestPasswordReset({ email }, captcha)
      if (authError) {
        setError(authError.message ?? 'Could not resend the code. Try again.')
        return
      }
      setCooldown(RESEND_COOLDOWN_SEC)
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)))
      setError('Could not resend the code. Try again.')
    } finally {
      setResending(false)
    }
  }

  const resendLabel = resending
    ? 'Sending...'
    : cooldown > 0
      ? `Resend in ${cooldown}s`
      : 'Resend code'

  return (
    <div className='mx-auto w-full max-w-[420px]'>
      <h1 className='font-display text-[36px] leading-[1.1] tracking-[-0.5px] text-ink'>
        Reset your password
      </h1>
      <p className='mt-2 text-[15px] text-ink-soft'>
        Enter the code we sent to{' '}
        <strong className='break-all text-ink'>{email}</strong> and choose a new
        password.
      </p>

      <form onSubmit={handleSubmit} className='mt-8 flex flex-col gap-4'>
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='code'>Verification code</Label>
          <InputOTP
            id='code'
            maxLength={OTP_LENGTH}
            value={code}
            onChange={setCode}
            disabled={submitting}
            autoFocus
          >
            <InputOTPGroup>
              {Array.from({ length: OTP_LENGTH }, (_, i) => (
                <InputOTPSlot key={i} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>
        </div>
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='password'>New password</Label>
          <PasswordInput
            id='password'
            autoComplete='new-password'
            placeholder='At least 8 characters'
            value={password}
            onChange={setPassword}
          />
        </div>
        <div className='flex flex-col gap-1.5'>
          <Label htmlFor='confirm'>Confirm password</Label>
          <PasswordInput
            id='confirm'
            autoComplete='new-password'
            value={confirm}
            onChange={setConfirm}
          />
        </div>
        {error && <p className='text-sm text-red-600'>{error}</p>}
        <button
          type='submit'
          disabled={
            code.length !== OTP_LENGTH ||
            password.length === 0 ||
            confirm.length === 0 ||
            submitting
          }
          className='mt-2 w-full rounded-xl bg-accent py-3.5 text-[15px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent'
        >
          {submitting ? 'Saving...' : 'Save new password'}
        </button>
      </form>

      <div className='mt-6 flex justify-center'>
        <TurnstileWidget />
      </div>
      <p className='mt-2 text-center text-sm text-ink-soft'>
        Didn&apos;t get it?{' '}
        <button
          type='button'
          onClick={() => void handleResend()}
          disabled={resending || cooldown > 0 || submitting}
          className='inline-flex min-h-11 cursor-pointer items-center px-3 py-2 font-medium text-accent transition-colors hover:text-[#15296b] disabled:cursor-not-allowed disabled:text-ink-muted dark:text-accent-mid'
        >
          {resendLabel}
        </button>
      </p>

      <p className='mt-8 text-center text-sm text-ink-soft'>
        Remembered it?{' '}
        <Link
          to='/login'
          className='font-medium text-accent dark:text-accent-mid'
        >
          Login
        </Link>
      </p>
    </div>
  )
}
